import React, { useState, useEffect, useMemo } from 'react';

// mui
import {
    Box,
    Checkbox,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    Chip,
} from '@mui/material';

// api
import { AdminObjectList, AdminObjectRemove, AdminObjectUpdate } from '../../../api';

// components
import { AdminReportInfoPanel, CommonYesNoDialog, AdminMenuBar, PillTabs } from '../..';

import { storage } from '@goldblack/client-storage';
import { ContainerScroll } from '@goldblack/client-comps';
import { def } from '@goldblack/shared';

const { ReportStatus } = def;

const STATUS_COLOR = {
    [ReportStatus.PENDING]: 'warning',
    [ReportStatus.RESOLVED]: 'success',
    [ReportStatus.DISMISSED]: 'default',
    [ReportStatus.DELETED]: 'error',
};

const STATUS_LABEL = {
    [ReportStatus.PENDING]: 'pending',
    [ReportStatus.RESOLVED]: 'resolved',
    [ReportStatus.DISMISSED]: 'dismissed',
    [ReportStatus.DELETED]: 'deleted',
};

function ReportItem({ report, selected, checked, onCheck, onClick }) {
    const reporterName = report.reporterUser?.username || report.reporter;
    const dateStr = report.report_at ? new Date(report.report_at).toLocaleString('ko-KR') : '';

    return (
        <TableRow selected={selected} hover onClick={onClick} sx={{ cursor: 'pointer' }}>
            <TableCell padding='checkbox' onClick={(e) => e.stopPropagation()}>
                <Checkbox size='small' checked={checked} onChange={onCheck} />
            </TableCell>
            <TableCell>
                <Typography variant='body2' noWrap color='primary.main'>
                    {reporterName}
                </Typography>
            </TableCell>
            <TableCell>
                <Chip
                    label={STATUS_LABEL[report.status] ?? report.status}
                    size='small'
                    color={STATUS_COLOR[report.status] || 'default'}
                />
            </TableCell>
            <TableCell sx={{ width: 120 }}>
                <Typography variant='caption' color='text.secondary' noWrap>
                    {dateStr}
                </Typography>
            </TableCell>
        </TableRow>
    );
}

const REPORT_TABS = [
    { value: 'user', label: 'User' },
    { value: 'comment', label: 'Comment' },
    { value: 'message', label: 'Message' },
    { value: 'post', label: 'Post' },
];

function LoiseReports(props) {
    const [type, setType] = useState('comment');
    const { loise } = storage.transience.getValue();
    const [selectedId, setSelectedId] = useState(null);
    const [openDelete, setOpenDelete] = useState(false);
    const [pendingStatus, setPendingStatus] = useState(null);
    const [checkedIds, setCheckedIds] = useState(() => new Set());
    const [sortBy, setSortBy] = useState('date');
    const [sortDir, setSortDir] = useState('desc');

    const allReports = loise.report.data || [];

    const sortedReports = useMemo(() => {
        const byType = allReports.filter((r) => (r.type || 'user') === type);
        return [...byType].sort((a, b) => {
            let cmp = 0;
            if (sortBy === 'reporter') {
                const an = (a.reporterUser?.username || a.reporter || '').toLowerCase();
                const bn = (b.reporterUser?.username || b.reporter || '').toLowerCase();
                cmp = an.localeCompare(bn);
            } else if (sortBy === 'status') {
                cmp = a.status - b.status;
            } else {
                cmp = (a.report_at || 0) - (b.report_at || 0);
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [allReports, type, sortBy, sortDir]);

    const getReport = () => sortedReports.find((r) => r._id === selectedId) || null;
    const checkedReports = sortedReports.filter((r) => checkedIds.has(r._id));
    const allChecked = sortedReports.length > 0 && checkedReports.length === sortedReports.length;
    const someChecked = checkedReports.length > 0;

    useEffect(() => {
        setSelectedId(null);
        setPendingStatus(null);
        setCheckedIds(new Set());
    }, [type]);

    const toggleChecked = (id) =>
        setCheckedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const toggleAll = () => setCheckedIds(allChecked ? new Set() : new Set(sortedReports.map((r) => r._id)));

    const onSort = (col) => {
        if (sortBy === col) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(col);
            setSortDir('asc');
        }
    };

    const onClickRefresh = () => AdminObjectList('loise', 'report');

    const onClickUpdate = () => {
        if (pendingStatus === null) return;
        const targets = someChecked ? checkedReports : [getReport()].filter(Boolean);
        targets.forEach((r) => AdminObjectUpdate('loise', 'report', { ...r, status: pendingStatus }));
        setPendingStatus(null);
        setCheckedIds(new Set());
    };

    const onClickDelete = () => {
        const targets = someChecked ? checkedReports : [getReport()].filter(Boolean);
        if (targets.length) setOpenDelete(true);
    };

    const onConfirmDelete = () => {
        setOpenDelete(false);
        const targets = someChecked ? checkedReports : [getReport()].filter(Boolean);
        targets.forEach((r) => AdminObjectRemove('loise', 'report', r._id));
        setCheckedIds(new Set());
    };

    useEffect(() => {
        AdminObjectList('loise', 'report');
    }, []);

    const apiItems = [
        { label: 'Refresh', onClick: onClickRefresh },
        { label: 'Update', onClick: onClickUpdate },
        { label: 'Delete', onClick: onClickDelete },
    ];

    const editItems = [
        { label: 'Pending', onClick: () => setPendingStatus(ReportStatus.PENDING) },
        { label: 'Resolved', onClick: () => setPendingStatus(ReportStatus.RESOLVED) },
        { label: 'Dismissed', onClick: () => setPendingStatus(ReportStatus.DISMISSED) },
    ];

    const deleteLabel = someChecked
        ? `Delete ${checkedReports.length} report${checkedReports.length > 1 ? 's' : ''}`
        : 'Delete this report';

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <PillTabs value={type} onChange={setType} tabs={REPORT_TABS} />
            <Stack sx={{ p: 0, m: 0, flex: 1, flexDirection: 'column', minHeight: 0 }}>
                <AdminMenuBar apiItems={apiItems} editItems={editItems} />
                <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden', height: '100%' }}>
                    {/* List panel */}
                    <Box
                        sx={{
                            width: '50%',
                            borderRight: 1,
                            borderColor: 'primary.main',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}>
                        <ContainerScroll>
                            <TableContainer>
                                <Table size='small' stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding='checkbox'>
                                                <Checkbox
                                                    size='small'
                                                    checked={allChecked}
                                                    indeterminate={someChecked && !allChecked}
                                                    onChange={toggleAll}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortBy === 'reporter'}
                                                    direction={sortBy === 'reporter' ? sortDir : 'asc'}
                                                    onClick={() => onSort('reporter')}>
                                                    Reporter
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortBy === 'status'}
                                                    direction={sortBy === 'status' ? sortDir : 'asc'}
                                                    onClick={() => onSort('status')}>
                                                    Status
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell sx={{ width: 120 }}>
                                                <TableSortLabel
                                                    active={sortBy === 'date'}
                                                    direction={sortBy === 'date' ? sortDir : 'asc'}
                                                    onClick={() => onSort('date')}>
                                                    Date
                                                </TableSortLabel>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedReports.map((report) => (
                                            <ReportItem
                                                key={report._id}
                                                report={report}
                                                selected={selectedId === report._id}
                                                checked={checkedIds.has(report._id)}
                                                onCheck={() => toggleChecked(report._id)}
                                                onClick={() => {
                                                    setSelectedId(report._id);
                                                    setPendingStatus(null);
                                                }}
                                            />
                                        ))}
                                        {sortedReports.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4}>
                                                    <Typography variant='body2' color='text.secondary'>
                                                        No reports
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </ContainerScroll>
                    </Box>
                    {/* Detail panel */}
                    <Box sx={{ width: '50%', overflow: 'auto' }}>
                        <AdminReportInfoPanel
                            report={getReport()}
                            pendingStatus={pendingStatus}
                            onSetStatus={setPendingStatus}
                            onDeepLink={props.onDeepLink}
                        />
                    </Box>
                </Box>

                <CommonYesNoDialog
                    open={openDelete}
                    onClose={() => setOpenDelete(false)}
                    title='Delete Report'
                    desc={deleteLabel + '. Are you sure?'}
                    onYes={onConfirmDelete}
                    onNo={() => setOpenDelete(false)}
                />
            </Stack>
        </Box>
    );
}

export { LoiseReports };
